<template>
  <div class="action-warehouse-page">
    <div class="top-panel">
      <div>
        <div class="page-title">动作仓库管理</div>
        <div class="page-desc">选择产品分类和仓库形态后，维护该形态下的仓库动作。</div>
      </div>
      <div class="top-actions">
        <el-button icon="el-icon-refresh" @click="refreshAll">刷新</el-button>
        <el-button type="primary" icon="el-icon-plus" :disabled="!selectedShapeId" @click="openAddDialog">新增动作</el-button>
      </div>
    </div>

    <div class="filter-panel">
      <div class="filter-item">
        <div class="filter-label">产品分类</div>
        <el-select v-model="productType" placeholder="请选择产品分类" @change="changeProductType">
          <el-option
            v-for="item in productTypes"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          ></el-option>
        </el-select>
      </div>
      <div class="filter-item wide">
        <div class="filter-label">形态搜索</div>
        <el-input
          v-model="shapeKeyword"
          clearable
          placeholder="输入形态名称搜索"
          @keyup.enter.native="loadShapeOptions"
          @clear="loadShapeOptions"
        >
          <el-button slot="append" icon="el-icon-search" @click="loadShapeOptions"></el-button>
        </el-input>
      </div>
      <div class="filter-item wide">
        <div class="filter-label">动作搜索</div>
        <el-input
          v-model="actionKeyword"
          clearable
          placeholder="输入动作名称搜索"
          :disabled="!selectedShapeId"
          @keyup.enter.native="loadActionList"
          @clear="loadActionList"
        >
          <el-button slot="append" icon="el-icon-search" :disabled="!selectedShapeId" @click="loadActionList"></el-button>
        </el-input>
      </div>
      <div class="filter-item">
        <div class="filter-label">动作状态</div>
        <el-select v-model="statusFilter" :disabled="!selectedShapeId" @change="loadActionList">
          <el-option label="全部" :value="0"></el-option>
          <el-option label="正常" :value="1"></el-option>
          <el-option label="下架" :value="2"></el-option>
          <el-option label="已删除" :value="3"></el-option>
        </el-select>
      </div>
    </div>

    <div class="summary-row">
      <div class="summary-card">
        <div class="summary-label">当前分类</div>
        <div class="summary-value">{{ currentProductTypeName }}</div>
      </div>
      <div class="summary-card">
        <div class="summary-label">形态数量</div>
        <div class="summary-value">{{ shapeOptions.length }}</div>
      </div>
      <div class="summary-card">
        <div class="summary-label">当前动作</div>
        <div class="summary-value">{{ actionList.length }}</div>
      </div>
    </div>

    <div class="workspace">
      <div class="shape-panel" v-loading="shapeLoading">
        <div class="section-head">
          <div>
            <div class="section-title">仓库形态</div>
            <div class="section-subtitle">shape_source_id 使用所选形态的 ID</div>
          </div>
        </div>
        <div v-if="shapeOptions.length" class="shape-list">
          <div
            v-for="item in shapeOptions"
            :key="item.id"
            :class="['shape-item', selectedShapeId === item.id ? 'active' : '']"
            @click="selectShape(item)"
          >
            <div class="shape-thumb">
              <img v-if="item.img" :src="item.img" />
              <span v-else>{{ getFirstChar(getShapeName(item)) }}</span>
            </div>
            <div class="shape-info">
              <div class="shape-name">{{ getShapeName(item) }}</div>
              <div class="shape-meta">ID {{ item.id }} · {{ currentProductTypeName }}</div>
            </div>
          </div>
        </div>
        <div v-else-if="!shapeLoading" class="empty-box">暂无形态数据</div>
      </div>

      <div class="action-panel" v-loading="actionLoading">
        <div class="section-head">
          <div>
            <div class="section-title">仓库动作</div>
            <div class="section-subtitle">
              {{ selectedShapeId ? ('当前形态：' + getShapeName(selectedShape)) : '请先选择一个形态' }}
            </div>
          </div>
          <el-button type="primary" icon="el-icon-plus" :disabled="!selectedShapeId" @click="openAddDialog">新增动作</el-button>
        </div>

        <div v-if="actionList.length" class="action-grid">
          <div
            v-for="item in actionList"
            :key="item.id"
            class="action-card"
            @click="openEditDialog(item)"
          >
            <div class="action-image-wrap">
              <img v-if="item.img" :src="item.img" class="action-image" />
              <div v-else class="action-image empty-image">{{ getFirstChar(item.name) }}</div>
              <div :class="['status-tag', getStatusClass(item.status)]">{{ getStatusText(item.status) }}</div>
            </div>
            <div class="action-body">
              <div class="action-name">{{ item.name || '未命名动作' }}</div>
              <div class="action-meta">ID {{ item.id }} · 形态 {{ item.shape_source_id }}</div>
              <div class="action-img-text">{{ item.img || '未填写图片地址' }}</div>
              <div class="action-img-text">{{ item.apmg ? ('APMG ' + item.apmg) : '未填写 APMG 地址' }}</div>
            </div>
          </div>
        </div>

        <div v-else-if="!actionLoading" class="empty-box">
          {{ selectedShapeId ? '当前形态还没有仓库动作' : '选择形态后显示动作列表' }}
        </div>
      </div>
    </div>

    <el-dialog
      :title="dialogMode === 'add' ? '新增仓库动作' : '编辑仓库动作'"
      :visible.sync="dialogVisible"
      width="680px"
    >
      <div class="dialog-shape">
        <div class="dialog-shape-title">{{ getShapeName(selectedShape) || '未选择形态' }}</div>
        <div class="dialog-shape-meta">product_type {{ productType }}，shape_source_id {{ selectedShapeId || 0 }}</div>
      </div>
      <el-form label-width="100px">
        <el-form-item label="动作名称">
          <el-input v-model="form.name" placeholder="请输入动作名称"></el-input>
        </el-form-item>
        <el-form-item label="图片地址">
          <el-input
            v-model="form.img"
            type="textarea"
            :rows="4"
            placeholder="请输入图片地址"
          ></el-input>
        </el-form-item>
        <el-form-item label="APMG地址">
          <el-input
            v-model="form.apmg"
            type="textarea"
            :rows="4"
            placeholder="请输入 APMG 图片地址"
          ></el-input>
        </el-form-item>
      </el-form>
      <div v-if="form.img" class="dialog-preview">
        <img :src="form.img" />
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button v-if="dialogMode === 'edit' && form.status !== 3" type="danger" plain :loading="statusLoading" @click="setCurrentStatus(3)">删除</el-button>
        <el-button v-if="dialogMode === 'edit' && form.status === 1" plain :loading="statusLoading" @click="setCurrentStatus(2)">下架</el-button>
        <el-button v-if="dialogMode === 'edit' && form.status !== 1" plain :loading="statusLoading" @click="setCurrentStatus(1)">上架</el-button>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitLoading" @click="submitAction">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  data() {
    return {
      apiBase: 'https://tossp.yoooooo.com/index.php/index/admin_product_action',
      productTypes: [
        { value: 1, label: '1 智体' },
        { value: 2, label: '2 空间' },
        { value: 3, label: '3 暗物质' },
        { value: 4, label: '4 万物' },
        { value: 5, label: '5 星球' },
        { value: 6, label: '6 星系' },
        { value: 7, label: '7 宇宙' },
        { value: 8, label: '8 之幂' },
        { value: 9, label: '9 无限' }
      ],
      productType: 1,
      shapeKeyword: '',
      actionKeyword: '',
      statusFilter: 0,
      shapeLoading: false,
      actionLoading: false,
      submitLoading: false,
      statusLoading: false,
      shapeOptions: [],
      selectedShapeId: 0,
      selectedShape: {},
      actionList: [],
      dialogVisible: false,
      dialogMode: 'add',
      form: {
        id: 0,
        name: '',
        img: '',
        apmg: '',
        status: 1
      }
    }
  },
  computed: {
    currentProductTypeName() {
      for (var i = 0; i < this.productTypes.length; i++) {
        if (this.productTypes[i].value === this.productType) {
          return this.productTypes[i].label
        }
      }
      return ''
    }
  },
  created() {
    this.loadShapeOptions()
  },
  methods: {
    changeProductType() {
      this.selectedShapeId = 0
      this.selectedShape = {}
      this.actionList = []
      this.loadShapeOptions()
    },
    refreshAll() {
      this.loadShapeOptions()
      if (this.selectedShapeId) {
        this.loadActionList()
      }
    },
    async loadShapeOptions() {
      this.shapeLoading = true
      try {
        const res = await axios.post(this.apiBase + '/warehouseShapeOptions', {
          product_type: this.productType,
          name: this.shapeKeyword
        })
        if (res.data.code === 200) {
          this.shapeOptions = res.data.data || []
          if (this.selectedShapeId) {
            this.syncSelectedShape()
          }
        } else {
          this.$message.error(res.data.msg || '形态加载失败')
        }
      } catch (e) {
        this.$message.error('形态加载失败')
      }
      this.shapeLoading = false
    },
    syncSelectedShape() {
      var found = {}
      for (var i = 0; i < this.shapeOptions.length; i++) {
        if (this.shapeOptions[i].id === this.selectedShapeId) {
          found = this.shapeOptions[i]
          break
        }
      }
      this.selectedShape = found
      if (!found.id) {
        this.selectedShapeId = 0
        this.actionList = []
      }
    },
    selectShape(item) {
      this.selectedShapeId = item.id
      this.selectedShape = item
      this.loadActionList()
    },
    async loadActionList() {
      if (!this.selectedShapeId) {
        this.actionList = []
        return
      }
      this.actionLoading = true
      try {
        const res = await axios.post(this.apiBase + '/warehouseActionList', {
          product_type: this.productType,
          shape_source_id: this.selectedShapeId,
          name: this.actionKeyword,
          status: this.statusFilter
        })
        if (res.data.code === 200) {
          this.actionList = res.data.data || []
        } else {
          this.$message.error(res.data.msg || '动作加载失败')
        }
      } catch (e) {
        this.$message.error('动作加载失败')
      }
      this.actionLoading = false
    },
    openAddDialog() {
      if (!this.selectedShapeId) {
        this.$message.error('请先选择形态')
        return
      }
      this.dialogMode = 'add'
      this.form = {
        id: 0,
        name: '',
        img: '',
        apmg: '',
        status: 1
      }
      this.dialogVisible = true
    },
    openEditDialog(item) {
      this.dialogMode = 'edit'
      this.form = {
        id: item.id,
        name: item.name || '',
        img: item.img || '',
        apmg: item.apmg || '',
        status: Number(item.status || 1)
      }
      this.dialogVisible = true
    },
    async submitAction() {
      if (!this.selectedShapeId) {
        this.$message.error('请先选择形态')
        return
      }
      if (!this.form.name) {
        this.$message.error('请输入动作名称')
        return
      }
      this.submitLoading = true
      try {
        var payload = {
          id: this.form.id,
          product_type: this.productType,
          shape_source_id: this.selectedShapeId,
          name: this.form.name,
          img: this.form.img,
          apmg: this.form.apmg
        }
        var url = this.dialogMode === 'add'
          ? this.apiBase + '/warehouseActionCreate'
          : this.apiBase + '/warehouseActionUpdate'
        const res = await axios.post(url, payload)
        if (res.data.code === 200) {
          this.$message.success(this.dialogMode === 'add' ? '新增成功' : '保存成功')
          this.dialogVisible = false
          this.loadActionList()
        } else {
          this.$message.error(res.data.msg || '保存失败')
        }
      } catch (e) {
        this.$message.error('保存失败')
      }
      this.submitLoading = false
    },
    setCurrentStatus(status) {
      if (!this.form.id) {
        return
      }
      var text = status === 3 ? '删除' : (status === 2 ? '下架' : '上架')
      var _this = this
      this.$confirm('确认' + text + '该动作？', '提示', {
        type: status === 3 ? 'warning' : 'info'
      }).then(async function() {
        _this.statusLoading = true
        try {
          const res = await axios.post(_this.apiBase + '/warehouseActionSetStatus', {
            id: _this.form.id,
            status: status
          })
          if (res.data.code === 200) {
            _this.$message.success(text + '成功')
            _this.dialogVisible = false
            _this.loadActionList()
          } else {
            _this.$message.error(res.data.msg || (text + '失败'))
          }
        } catch (e) {
          _this.$message.error(text + '失败')
        }
        _this.statusLoading = false
      }).catch(function() {})
    },
    getShapeName(item) {
      if (!item) {
        return ''
      }
      return item.namech || item.name || ''
    },
    getFirstChar(name) {
      if (!name) {
        return '形'
      }
      return String(name).slice(0, 1)
    },
    getStatusText(status) {
      var value = Number(status || 1)
      if (value === 2) {
        return '下架'
      }
      if (value === 3) {
        return '已删除'
      }
      return '正常'
    },
    getStatusClass(status) {
      var value = Number(status || 1)
      if (value === 2) {
        return 'off'
      }
      if (value === 3) {
        return 'deleted'
      }
      return 'normal'
    }
  }
}
</script>

<style scoped>
.action-warehouse-page {
  min-height: 100vh;
  padding: 24px;
  background: #f3f6fa;
  box-sizing: border-box;
}

.action-warehouse-page,
.action-warehouse-page * {
  box-sizing: border-box;
}

.top-panel {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 22px 24px;
  margin-bottom: 18px;
  border-radius: 8px;
  background: #ffffff;
  border-left: 5px solid #2f80ed;
  box-shadow: 0 8px 24px rgba(31, 45, 61, 0.06);
}

.page-title {
  color: #1f2d3d;
  font-size: 24px;
  font-weight: 700;
}

.page-desc {
  margin-top: 8px;
  color: #7d8ba3;
  font-size: 14px;
}

.top-actions {
  display: flex;
  gap: 10px;
}

.filter-panel {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  padding: 18px;
  margin-bottom: 18px;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 8px 24px rgba(31, 45, 61, 0.05);
}

.filter-item {
  width: 180px;
}

.filter-item.wide {
  width: 280px;
}

.filter-label {
  margin-bottom: 8px;
  color: #66758c;
  font-size: 13px;
}

.summary-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 18px;
}

.summary-card {
  min-width: 190px;
  padding: 18px 20px;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 8px 24px rgba(31, 45, 61, 0.05);
}

.summary-label {
  color: #8a94a6;
  font-size: 13px;
}

.summary-value {
  margin-top: 8px;
  color: #1f2d3d;
  font-size: 24px;
  font-weight: 700;
}

.workspace {
  display: grid;
  grid-template-columns: 360px 1fr;
  gap: 18px;
}

.shape-panel,
.action-panel {
  min-height: 520px;
  padding: 18px;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 8px 24px rgba(31, 45, 61, 0.05);
}

.section-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.section-title {
  color: #1f2d3d;
  font-size: 18px;
  font-weight: 700;
}

.section-subtitle {
  margin-top: 6px;
  color: #8a94a6;
  font-size: 13px;
}

.shape-list {
  max-height: 680px;
  overflow: auto;
}

.shape-item {
  display: flex;
  align-items: center;
  padding: 10px;
  margin-bottom: 10px;
  border: 1px solid #edf0f5;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.18s ease, background 0.18s ease;
}

.shape-item:hover,
.shape-item.active {
  border-color: #2f80ed;
  background: #f2f7ff;
}

.shape-thumb {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 58px;
  height: 58px;
  flex-shrink: 0;
  overflow: hidden;
  border-radius: 8px;
  background: #edf3fb;
  color: #2f80ed;
  font-size: 22px;
  font-weight: 700;
}

.shape-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.shape-info {
  min-width: 0;
  margin-left: 12px;
}

.shape-name {
  color: #1f2d3d;
  font-size: 15px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.shape-meta {
  margin-top: 8px;
  color: #8a94a6;
  font-size: 12px;
}

.action-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
}

.action-card {
  overflow: hidden;
  border: 1px solid #edf0f5;
  border-radius: 8px;
  background: #ffffff;
  cursor: pointer;
  transition: box-shadow 0.18s ease, transform 0.18s ease;
}

.action-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 28px rgba(31, 45, 61, 0.1);
}

.action-image-wrap {
  position: relative;
  height: 156px;
  background: #eef3f8;
}

.action-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.empty-image {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #2f80ed;
  font-size: 44px;
  font-weight: 700;
}

.status-tag {
  position: absolute;
  top: 10px;
  right: 10px;
  height: 24px;
  line-height: 24px;
  padding: 0 10px;
  border-radius: 6px;
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
}

.status-tag.normal {
  background: #20a162;
}

.status-tag.off {
  background: #e6a23c;
}

.status-tag.deleted {
  background: #909399;
}

.action-body {
  padding: 14px;
}

.action-name {
  color: #1f2d3d;
  font-size: 16px;
  font-weight: 700;
  word-break: break-all;
}

.action-meta {
  margin-top: 8px;
  color: #2f80ed;
  font-size: 12px;
  font-weight: 700;
}

.action-img-text {
  height: 36px;
  margin-top: 8px;
  color: #8a94a6;
  font-size: 12px;
  line-height: 18px;
  overflow: hidden;
  word-break: break-all;
}

.empty-box {
  padding: 54px 20px;
  border-radius: 8px;
  background: #f8fafc;
  text-align: center;
  color: #8a94a6;
  font-size: 14px;
}

.dialog-shape {
  padding: 12px 14px;
  margin-bottom: 18px;
  border-radius: 8px;
  background: #f5f8fc;
}

.dialog-shape-title {
  color: #1f2d3d;
  font-size: 16px;
  font-weight: 700;
}

.dialog-shape-meta {
  margin-top: 6px;
  color: #8a94a6;
  font-size: 13px;
}

.dialog-preview {
  width: 180px;
  height: 120px;
  margin-left: 100px;
  overflow: hidden;
  border-radius: 8px;
  background: #eef3f8;
}

.dialog-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

@media screen and (max-width: 980px) {
  .top-panel,
  .section-head {
    display: block;
  }

  .top-actions {
    margin-top: 16px;
  }

  .workspace {
    grid-template-columns: 1fr;
  }

  .filter-item,
  .filter-item.wide {
    width: 100%;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/main/xingxiangAction.vue